import { useT } from '@eturi/react-i18next'
import LOGO from '@op/assets/logos/logo.svg'
import type { CssGlyphName } from '@op/icons'
import { useHandleSynPrevent } from '@op/react-web'
import { isAuthenticated$, needsEmailVerification$ } from '@op/services'
import type { MouseEventHandler, ReactNode } from 'react'
import { memo } from 'react'
import { useSelector } from 'react-redux'
import { baseLink$ } from '../../compound-selectors/app-misc'
import { MOBILE_MENU_WIDTH } from '../../constants/sizes'
import { SUPPORT_URL } from '../../constants/urls'
import { ZTopNav } from '../../constants/z-index'
import { createStyles } from '../../styles/createStyles'
import { setPairDeviceView } from '../../thunks/pair-device'
import { useAppDispatch } from '../../types'
import { Glyph, NAV_TRANSITION_MS, OPLink, useNav, WarningBadge } from '../../widgets'

const MobileNav = () => {
	const [t] = useT('menu_items')
	const d = useAppDispatch()
	const baseLink = useSelector(baseLink$)
	const isAuthenticated = useSelector(isAuthenticated$)
	const nav = useNav()
	const needsEmailVerification = useSelector(needsEmailVerification$)

	// We instead intercept and open the pairing modal. I'm not sure why we keep the link.
	const handlePairDeviceClick = useHandleSynPrevent(() => {
		d(setPairDeviceView({ view: 'pair_comparison' }))
	})

	if (!(isAuthenticated && nav.isMobile)) return null

	return (
		<nav
			className="flex flex-col pt-4 pb-2 fixed bottom-0 left-0 top-0 bg-teal text-white"
			style={nav.isOpen ? styles.menuVisible : styles.menuHidden}
		>
			<div className="flex-center mb-6">
				<OPLink className="flex" to={baseLink} aria-label={t.$('navigation.home_logo_label')}>
					<span className="bg-white flex-center pt-0.5 h-16 w-16 rounded-xl">
						<img className="h-16 w-16" alt="OurPact Logo" src={LOGO} />
					</span>
				</OPLink>
			</div>

			<div className="py-2 touch-scroll-y">
				<MobileMenuItem icon="add-device" link="/connect" onClick={handlePairDeviceClick}>
					{t('pair_device')}
				</MobileMenuItem>

				<MobileMenuItem icon="people-outline" link="/my-family">
					{t('my_family')}
				</MobileMenuItem>

				<MobileMenuItem
					badge={!needsEmailVerification ? <WarningBadge className="h-full w-full" /> : null}
					icon="user"
					link="/account"
				>
					{t('account')}
				</MobileMenuItem>

				{process.env.APP_ENV === 'dev' && (
					<MobileMenuItem icon="layers" link="/demo">
						Demo
					</MobileMenuItem>
				)}

				<MobileMenuItem icon="help" link={SUPPORT_URL}>
					{t('help')}
				</MobileMenuItem>

				<MobileMenuItem icon="power" link="/logout">
					{t('logout')}
				</MobileMenuItem>
			</div>
		</nav>
	)
}

// NOTE: We have to change visibility as well so keyboard users can't tab to
//  menu elements that are not visible.
const styles = createStyles({
	menuHidden: {
		transform: `translate3d(-${MOBILE_MENU_WIDTH}px, 0, 1px)`,
		transition: `transform ${NAV_TRANSITION_MS}ms linear, visibility ${NAV_TRANSITION_MS}ms linear`,
		visibility: 'hidden',
		width: `${MOBILE_MENU_WIDTH}px`,
		// Make sure this is over the top nav, otherwise we can't click the entire OP logo
		zIndex: ZTopNav,
	},

	menuVisible: {
		transform: `translate3d(0, 0, 1px)`,
		transition: `transform ${NAV_TRANSITION_MS}ms linear, visibility 0s linear`,
		visibility: 'visible',
		width: `${MOBILE_MENU_WIDTH}px`,
		zIndex: ZTopNav,
	},
})

export default /*@__PURE__*/ memo(MobileNav)

type MobileMenuItemProps = {
	readonly badge?: ReactNode
	readonly children?: ReactNode
	readonly icon: CssGlyphName
	readonly link: string
	readonly onClick?: MouseEventHandler<HTMLAnchorElement>
}

const MobileMenuItem = ({ badge, children, icon, link, onClick }: MobileMenuItemProps) => (
	<OPLink
		className="flex items-center p-4 text-white hover:text-gray-95"
		onClick={onClick}
		to={link}
	>
		<span className="flex-center relative z-10">
			<Glyph name={icon} />
			{badge && (
				<span className="absolute border border-teal h-6 w-6 rounded-full -top-1.5 -right-3.5 md:-right-4">
					{badge}
				</span>
			)}
		</span>
		<span className="font-bold ml-5 leading-5">{children}</span>
	</OPLink>
)
